import { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import CommonLoading from "../components/loader/CommonLoading";
import ContentWithCards from "../components/shared/ContentWithCards";
import CommunityMainSection from "../components/community/MainSection";
import { getCommunityAction, leaveCommunityAction, getJoinedCommunitiesAction, joinCommunityAndFetchData, getComMembersAction } from "../redux/actions/communityActions";
import { getFollowingUsersAction } from "../redux/actions/userActions";
import { IoMdPeople } from "react-icons/io";
import placeholder from "../assets/placeholder.png";
import { RichTextContent } from "../utils/richTextProcessor";
import { useToast } from "../contexts/ToastContext";

const CommunityInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { communityName } = useParams();
  const { success: showSuccess, error: showError } = useToast();
  const [isLeaving, setIsLeaving] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeTab, setActiveTab] = useState("about");

  const { communityData, communityError, joinedCommunities, communityMembers } = useSelector((state) => state.community || {});
  const followingUsers = useSelector((state) => state.user?.followingUsers);
  const isMember = joinedCommunities?.some(
    ({ name }) => name === communityName
  );

  // Set default tab based on membership status
  useEffect(() => {
    if (joinedCommunities && communityName) {
      const memberStatus = joinedCommunities.some(({ name }) => name === communityName);
      if (memberStatus) {
        setActiveTab("feed");
      } else {
        setActiveTab("members");
      }
    }
  }, [joinedCommunities, communityName]);

  useEffect(() => {
    if (communityName && communityName !== 'undefined') {
      dispatch(getCommunityAction(communityName));
      dispatch(getJoinedCommunitiesAction());
      dispatch(getComMembersAction(communityName));
      dispatch(getFollowingUsersAction(25, 0)); // Fetch following users for sorting
    }
  }, [dispatch, communityName]);

  // Sort members for avatar display: admins first, then followed users, then alphabetically
  const sortedMembersForAvatars = useMemo(() => {
    if (!communityMembers?.members || communityMembers.members.length === 0) return [];

    const members = [...communityMembers.members];
    const followingUserIds = followingUsers?.map(user => user._id) || [];
    
    // Separate admins, followed members, and other members
    const admins = members.filter(member => 
      communityData?.moderators?.some(mod => mod._id === member._id || mod === member._id)
    );
    
    const followedMembers = members.filter(member => 
      !admins.some(admin => admin._id === member._id || admin === member._id) &&
      followingUserIds.includes(member._id)
    );
    
    const otherMembers = members.filter(member => 
      !admins.some(admin => admin._id === member._id || admin === member._id) &&
      !followedMembers.some(followed => followed._id === member._id || followed === member._id)
    );

    // Sort each group alphabetically by name
    const sortByName = (a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    };

    return [
      ...admins.sort(sortByName),
      ...followedMembers.sort(sortByName),
      ...otherMembers.sort(sortByName)
    ];
  }, [communityMembers?.members, communityData?.moderators, followingUsers]);

  // Get display members (max 5) and remaining count
  const displayMembers = useMemo(() => {
    return sortedMembersForAvatars.slice(0, 5);
  }, [sortedMembersForAvatars]);

  const remainingCount = Math.max(0, sortedMembersForAvatars.length - 5);

  const handleLeaveClick = () => {
    setShowConfirmModal(true);
  };

  const handleLeaveCommunity = async () => {
    if (isLeaving) return;
    
    setIsLeaving(true);
    try {
      await dispatch(leaveCommunityAction(communityName));
      showSuccess(`Successfully left ${communityName}!`);
      // Refresh joined communities after leaving
      dispatch(getJoinedCommunitiesAction());
      setShowConfirmModal(false);
      // Switch to members tab after leaving
      setActiveTab("members");
      // Redirect to My Communities page after leaving
      window.location.href = '/my-communities';
    } catch (error) {
      showError(error.message || "Failed to leave community. Please try again.");
      console.error('Error leaving community:', error);
    } finally {
      setIsLeaving(false);
    }
  };

  const handleCancelLeave = () => {
    setShowConfirmModal(false);
  };

  // Sort members: admins first, then alphabetically
  const sortedMembers = communityMembers?.members?.sort((a, b) => {
    const aIsAdmin = communityData.moderators?.some(mod => mod._id === a._id || mod === a._id);
    const bIsAdmin = communityData.moderators?.some(mod => mod._id === b._id || mod === b._id);
    
    if (aIsAdmin && !bIsAdmin) return -1;
    if (!aIsAdmin && bIsAdmin) return 1;
    
    return a.name.localeCompare(b.name);
  }) || [];

  const renderMembersGrid = () => {
    if (!sortedMembers || sortedMembers.length === 0) {
      return (
        <p className="text-sm text-gray-500 text-center py-8 font-roboto-flex">
          No members found.
        </p>
      );
    }
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sortedMembers.map((member) => {
          const isAdmin = communityData.moderators?.some(mod => mod._id === member._id || mod === member._id);
          
          return (
            <div 
              key={member._id} 
              className={`bg-white border rounded-md w-full px-4 py-4 shadow-2xl shadow-[#f2f5fc] relative cursor-pointer hover:shadow-lg hover:border-primary-300 transition-all duration-200 ${isAdmin ? 'border-yellow-400' : ''}`}
              onClick={() => navigate(`/profile/${member.profileUrl || member._id}`)}
            >
              {/* Admin Badge */}
              {isAdmin && (
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-200 rounded-full">
                    Admin
                  </span>
                </div>
              )}

              {/* Main Card Content */}
              <div className="flex gap-3 pr-8">
                <img
                  src={member.avatar || placeholder}
                  alt={member.name}
                  className="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <h2 className="font-bold text-base font-roboto-flex">{member.name}</h2>
                  <p className="flex items-center gap-2 font-roboto-flex text-sm text-gray-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {member.location || 
                     (member.city && member.country ? `${member.city}, ${member.country}` : 
                      member.city || member.country || "Location not set")}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleJoinCommunity = async () => {
    if (!communityName || isJoining) return;
    
    setIsJoining(true);
    try {
      await dispatch(joinCommunityAndFetchData(communityName));
      showSuccess(`Successfully joined ${communityName}!`);
      // Refresh joined communities after joining
      dispatch(getJoinedCommunitiesAction());
      // Switch to feed tab after joining
      setActiveTab("feed");
    } catch (error) {
      showError(error.message || "Failed to join community. Please try again.");
      console.error('Error joining community:', error);
    } finally {
      setIsJoining(false);
    }
  };

  if (!communityData || !joinedCommunities) {
    return (
      <ContentWithCards>
        <div className="main-section flex items-center justify-center h-screen">
          <CommonLoading />
        </div>
      </ContentWithCards>
    );
  }

  if (communityError) {
    return (
      <ContentWithCards>
        <div className="main-section flex items-center justify-center h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Community</h2>
            <p className="text-gray-500">{communityError}</p>
          </div>
        </div>
      </ContentWithCards>
    );
  }

  return (
    <ContentWithCards>
      <div className="main-section">
        {/* Header Card with Integrated Tabs - Only show for non-feed tabs */}
        {activeTab !== "feed" && (
          <div className="bg-white border rounded-lg shadow-sm mb-4">
            {/* Community Info Section */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <img
                  src={communityData.banner || placeholder}
                  alt={communityData.name}
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1">
                  <h1 className="text-lg font-semibold text-gray-800 font-roboto-flex">
                    {communityData.name}
                  </h1>
                  <p className="text-xs text-gray-500 font-roboto-flex mb-2">
                    {communityData.members?.length || 0} members
                  </p>
                  
                  {/* Member Avatars */}
                  {displayMembers.length > 0 && (
                    <div className="flex items-center gap-1">
                      {displayMembers.map((member, index) => (
                        <div key={member._id || member} className="relative">
                          <img
                            src={member.avatar || placeholder}
                            alt={member.name || 'Member'}
                            className="w-6 h-6 rounded-full object-cover border-2 border-white shadow-sm"
                            title={member.name || 'Member'}
                          />
                          {/* Admin indicator */}
                          {communityData?.moderators?.some(mod => mod._id === member._id || mod === member._id) && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border border-white flex items-center justify-center">
                              <span className="text-xs text-white font-bold">A</span>
                            </div>
                          )}
                        </div>
                      ))}
                      {remainingCount > 0 && (
                        <div className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600 font-roboto-flex">
                            +{remainingCount}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isMember ? (
                    <button 
                      onClick={handleLeaveClick}
                      disabled={isLeaving}
                      className="bg-action hover:bg-action-hover text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors font-roboto-flex disabled:opacity-50"
                    >
                      {isLeaving ? "Leaving..." : "Leave"}
                    </button>
                  ) : (
                    <button 
                      onClick={handleJoinCommunity}
                      disabled={isJoining}
                      className="bg-primary-700 hover:bg-primary-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors font-roboto-flex disabled:opacity-50"
                    >
                      {isJoining ? "Joining..." : "Join"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs Section */}
            <div className="px-4">
              <nav className="flex">
                {isMember && (
                  <button
                    onClick={() => setActiveTab("feed")}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors font-roboto-flex ${
                      activeTab === "feed"
                        ? "border-primary text-primary"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Feed
                  </button>
                )}
                <button
                  onClick={() => setActiveTab("members")}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors font-roboto-flex ${
                    activeTab === "members"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Members
                </button>
                <button
                  onClick={() => setActiveTab("about")}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors font-roboto-flex ${
                    activeTab === "about"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  About
                </button>
              </nav>
            </div>
          </div>
        )}

        {/* Content Cards */}
        {activeTab === "feed" && isMember ? (
          <CommunityMainSection />
        ) : (
          <div className="bg-white border rounded-lg shadow-sm">
            <div className="p-6">
              {activeTab === "members" && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <IoMdPeople className="text-lg text-gray-500" />
                    <h2 className="text-sm font-semibold text-gray-800 font-roboto-flex">
                      Members ({sortedMembers.length})
                    </h2>
                  </div>
                  {sortedMembers.length > 0 ? (
                    renderMembersGrid()
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-8 font-roboto-flex">
                      No members found.
                    </p>
                  )}
                </div>
              )}
              
              {activeTab === "about" && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-800 mb-4 font-roboto-flex">
                    About {communityData.name}
                  </h2>
                  <div className="prose max-w-none">
                    <div className="text-sm text-gray-700 leading-relaxed font-roboto-flex">
                      <RichTextContent 
                        content={communityData.description || communityData.about || "No description available for this community."} 
                      />
                    </div>
                  </div>
                  
                  {/* Community Rules */}
                  {communityData.rules && communityData.rules.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-base font-semibold text-gray-800 mb-3 font-roboto-flex">
                        Community Rules
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          {communityData.rules.map((rule, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-gray-700 font-roboto-flex">
                              <span className="text-primary font-bold">{index + 1}.</span>
                              <span>{rule}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 font-roboto-flex">
                Leave Community
              </h3>
              <p className="text-gray-600 mb-6 font-roboto-flex">
                Are you sure you want to leave {communityData.name}? You'll no longer see posts from this community.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelLeave}
                  disabled={isLeaving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors disabled:opacity-50 font-roboto-flex"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLeaveCommunity}
                  disabled={isLeaving}
                  className="px-4 py-2 bg-action text-white hover:bg-action-hover rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-roboto-flex"
                >
                  {isLeaving ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Leaving...</span>
                    </div>
                  ) : (
                    "Leave Community"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ContentWithCards>
  );
};

export default CommunityInfo;
